import { useRequiredContext } from "@redotech/react-util/context";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";
import { viewUrlNameToDisplayName } from "./view-name-utils";
import { DefaultViews, ViewsContext } from "./views";

export const CreateFolderModal = memo(function CreateFolderModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    folderName: string,
    selectedViewIds: string[],
    defaultViews: string[],
    isPrivate: boolean,
  ) => Promise<void>;
}) {
  const [folderName, setFolderName] = useState("");
  const [selectedViews, setSelectedViews] = useState<Record<string, boolean>>(
    {},
  );
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleViewSelect = useCallback((viewId: string, checked: boolean) => {
    setSelectedViews((prev) => ({ ...prev, [viewId]: checked }));
  }, []);

  const viewsContext = useRequiredContext(ViewsContext);

  // Combine context arrays for a full list of user/team views
  const allViews = [
    ...Object.values(DefaultViews).map((view) => ({
      _id: view,
      name: viewUrlNameToDisplayName(view),
    })),
    ...viewsContext.map((view) => ({ _id: view._id, name: view.name })),
  ];

  const hasSelectedViews = Object.values(selectedViews).some(Boolean);
  const submitDisabled =
    !folderName.trim().length || !hasSelectedViews || isSubmitting;

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const selectedIds = Object.entries(selectedViews)
        .filter(([_, checked]) => checked)
        .map(([viewId]) => viewId);
      const defaultViews = selectedIds.filter((id) =>
        Object.values(DefaultViews).includes(id as DefaultViews),
      );
      const customViews = selectedIds.filter(
        (id) => !Object.values(DefaultViews).includes(id as DefaultViews),
      );
      await onSubmit(folderName, customViews, defaultViews, isPrivate);
      onClose();
    } finally {
      setIsSubmitting(false);
      setFolderName("");
      setSelectedViews({});
      setIsPrivate(false);
    }
  };

  const primaryButton: RedoModalButtonProps = {
    text: "Create",
    onClick: handleSubmit,
    disabled: submitDisabled,
    size: "sm",
  };

  const secondaryButton: RedoModalButtonProps = {
    text: "Cancel",
    onClick: onClose,
    disabled: isSubmitting,
    size: "sm",
  };

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={isSubmitting ? () => {} : onClose}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      title="Create folder"
    >
      <Flex dir="column" gap="xl">
        <div data-devinid="folder-name-input">
          <RedoTextInput
            label="Folder name"
            setValue={setFolderName}
            size="sm"
            state={isSubmitting ? "readonly" : "default"}
            value={folderName}
          />
        </div>
        <Text>Select Views</Text>
        <Flex dir="column" gap="md" pl="sm">
          {allViews.map((view) => (
            <div key={view._id}>
              <Flex>
                <RedoCheckbox
                  label={view.name}
                  setValue={(val: CheckboxValue) =>
                    handleViewSelect(view._id, val === true)
                  }
                  size="md"
                  value={Boolean(selectedViews[view._id])}
                />
              </Flex>
            </div>
          ))}
        </Flex>
        <RedoCheckbox
          label="Private folder (only visible to you)"
          setValue={(val: CheckboxValue) => setIsPrivate(val === true)}
          size="md"
          value={isPrivate}
        />
      </Flex>
    </RedoModal>
  );
});
